/* === TDM GIFT / DISCOUNT WATCHER ===
   Diagnostic tool: some customers end up without the automatic free gift
   (Tek Labs Gifts & Upsell, internally still named "salepify" in its own
   endpoints/attributes) and it's never clear why. This writes evidence
   onto the cart attributes for three distinct failure modes — cart
   attributes carry over onto the resulting order's "Additional details"
   in the Shopify admin:

   1. removed  — a discount code / cart-level discount / free line item
      that was present in this session's cart has disappeared (customer
      action, or the cart dropped below the qualifying threshold).
   2. blocked  — the app's own script never made it into the page (ad
      blocker / privacy extension / consent tool filtering it out).
   3. proxy    — the app's script loaded fine but its own network calls
      (to /apps/salepify/... or the tek-labs-gifts-upsell extension) came
      back with an error — the app initialized but failed server-side.
   4. checkout — the customer heads to checkout (Shop Pay / Apple Pay /
      Google Pay / normal checkout button — anything that navigates away)
      while the cart still shows no free gift. Confirmed cause on this
      store: single-product orders via Shop Pay skip the cart page/drawer
      entirely, so the app never gets a chance to inject the gift.

   Each failure mode writes to its own attribute key so simultaneous
   signals in the same session don't overwrite each other. Always reads
   the current cart and merges into its existing attributes before
   writing, since /cart/update.js replaces the whole attributes set
   wholesale — never touches attributes other apps/scripts already set. */

(function () {
  'use strict';

  var STORAGE_KEY = 'tdmGiftWatcherState';
  var POLL_INTERVAL_MS = 6000;
  var APP_URL_PATTERN = /apps\/salepify|tek-labs-gifts-upsell/i;
  var pollTimer = null;
  var checking = false;
  var proxyIssueLogged = false;
  var checkoutBeaconSent = false;
  var lastSnapshot = null;
  var lastAttributes = {};

  function writeNote(attributeKey, note) {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var attributes = Object.assign({}, cart.attributes || {});
        attributes[attributeKey] = (new Date().toISOString() + ' | ' + note).slice(0, 250);
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attributes })
        });
      })
      .catch(function () {
        /* best-effort diagnostic only — never surface errors to the customer */
      });
  }

  /* --- 1. Removed discount / free item ------------------------------- */

  function readState() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch (e) {
      return null;
    }
  }

  function writeState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* sessionStorage unavailable (private mode, etc.) — silently skip */
    }
  }

  function snapshot(cart) {
    return {
      discountCodes: (cart.discount_codes || [])
        .filter(function (d) { return d.applicable; })
        .map(function (d) { return d.code; }),
      cartDiscounts: (cart.cart_level_discount_applications || [])
        .map(function (d) { return d.title; }),
      freeItems: (cart.items || [])
        .filter(function (i) { return i.final_price === 0 || i.price === 0; })
        .map(function (i) { return i.title; })
    };
  }

  function diffLost(previous, current) {
    var lost = [];
    ['discountCodes', 'cartDiscounts', 'freeItems'].forEach(function (key) {
      (previous[key] || []).forEach(function (value) {
        if (current[key].indexOf(value) === -1) {
          lost.push(value);
        }
      });
    });
    return lost;
  }

  function check() {
    if (checking) return;
    checking = true;
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var current = snapshot(cart);
        var previous = readState();
        if (previous) {
          var lost = diffLost(previous, current);
          if (lost.length) {
            writeNote('_gift_watch_removed', 'perdu: ' + lost.join(', '));
          }
        }
        writeState(current);
        lastSnapshot = current;
        lastAttributes = cart.attributes || {};
      })
      .catch(function () {})
      .then(function () {
        checking = false;
      });
  }

  function startPolling() {
    if (pollTimer) return;
    pollTimer = setInterval(function () {
      if (document.visibilityState === 'visible') check();
    }, POLL_INTERVAL_MS);
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') check();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, check);
  }

  check();
  startPolling();

  /* --- 2. App script blocked (ad blocker / consent tool) -------------- */
  /* Re-requests the exact same URL(s) the page already tried to load.
     A blocker that stops the app's own <script> tag will just as reliably
     stop our fetch of that identical URL, regardless of load timing. */

  function checkScriptBlocked() {
    var scripts = document.querySelectorAll('script[src]');
    var appScripts = [];
    scripts.forEach(function (s) {
      if (APP_URL_PATTERN.test(s.src)) appScripts.push(s.src);
    });

    if (appScripts.length === 0) {
      writeNote('_gift_watch_script', 'aucun script gift-app trouve dans la page (embed desactive ou retire avant chargement)');
      return;
    }

    appScripts.forEach(function (url) {
      fetch(url, { mode: 'no-cors', cache: 'no-store' }).catch(function () {
        writeNote('_gift_watch_script', 'script bloque (adblocker/consentement) : ' + url.split('/').pop());
      });
    });
  }

  setTimeout(checkScriptBlocked, 2500);

  /* --- 3. App's own network calls failing (proxy/backend error) ------- */
  /* Passively observes fetches to the gift app's endpoints without ever
     altering the request/response the app itself sees. */

  if (typeof window.fetch === 'function') {
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = originalFetch.apply(this, arguments);
      if (APP_URL_PATTERN.test(url)) {
        promise.then(function (response) {
          if (!proxyIssueLogged && response && !response.ok) {
            proxyIssueLogged = true;
            writeNote('_gift_watch_proxy', 'reponse ' + response.status + ' sur ' + url.split('?')[0]);
          }
        }).catch(function () {
          if (!proxyIssueLogged) {
            proxyIssueLogged = true;
            writeNote('_gift_watch_proxy', 'echec reseau sur ' + url.split('?')[0]);
          }
        });
      }
      return promise;
    };
  }

  /* --- 4. Heading to checkout without the gift ------------------------ */
  /* Scoped to actual checkout intent — NOT every page navigation — via two
     signals: (a) a real click on a checkout link/button (native "Commander"
     button, always in our own document) or (b) the pointer entering a
     .shopify-payment-button wrapper (Shop Pay / Apple Pay / Google Pay —
     the real button renders in a cross-origin iframe we can't get click
     events out of, so entering its wrapper is the closest we can observe).
     Only then does pagehide send the beacon — sendBeacon is the only
     mechanism the browser reliably lets finish while the page is closing,
     so it can't do the usual GET-then-merge round trip: it merges into the
     last cart snapshot our own polling already fetched (at most a few
     seconds old — the cart isn't expected to change between pressing a
     checkout button and the page unloading). */

  var CHECKOUT_INTENT_WINDOW_MS = 15000;
  var checkoutIntentAt = 0;

  function markCheckoutIntent() {
    checkoutIntentAt = Date.now();
  }

  document.addEventListener('click', function (event) {
    var el = event.target.closest('[name="checkout"], [href*="/checkout"], .cart__checkout-button, #checkout');
    if (el) markCheckoutIntent();
  }, true);

  document.addEventListener('mouseover', function (event) {
    if (event.target.closest && event.target.closest('.shopify-payment-button')) markCheckoutIntent();
  });

  document.addEventListener('touchstart', function (event) {
    var el = event.target.closest && event.target.closest('.shopify-payment-button');
    if (el) markCheckoutIntent();
  }, true);

  function sendCheckoutBeacon() {
    if (checkoutBeaconSent) return;
    if (!lastSnapshot) return;
    if (Date.now() - checkoutIntentAt > CHECKOUT_INTENT_WINDOW_MS) return;
    if (!lastSnapshot.freeItems.length && !lastSnapshot.discountCodes.length && !lastSnapshot.cartDiscounts.length) {
      var attributes = Object.assign({}, lastAttributes);
      attributes['_gift_watch_checkout'] =
        (new Date().toISOString() + ' | depart vers checkout sans cadeau/reduction').slice(0, 250);
      var blob = new Blob([JSON.stringify({ attributes: attributes })], { type: 'application/json' });
      if (navigator.sendBeacon('/cart/update.js', blob)) {
        checkoutBeaconSent = true;
      }
    }
  }

  window.addEventListener('pagehide', sendCheckoutBeacon);
})();
